import ChangeTripsModalAlert from '@/components/manage/change-trips/ChangeTripsModalAlert';
import TripOverview from '@/components/manage/change-trips/TripOverview';
import TripPassengerListPopover from '@/components/manage/change-trips/TripPassengerListPopover';
import ResponsiveDialog from '@/components/ResponsiveDialog';
import {
  resetChangeTripsSelection,
  type ChangeBookingFlow,
} from '@/features/manage/manageActions';
import {
  changeTripsOutboundTripIdSelector,
  changeTripsReturnTripIdSelector,
  manageFlowBookingSelector,
} from '@/features/manage/manageSelectors';
import { resetPurchase } from '@/features/purchase/purchaseActions';
import { useBookingTrips } from '@/hooks/useBookingTrips';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useChangeTravelClass } from '@/hooks/useChangeTravelClass';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { BookedTripDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import {
  isTripChangeDisabled,
  isTripWhollyInactive,
  type AlertReason,
} from '@/utils/changeTrips';
import { cn } from '@/utils/cn';
import {
  Button,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import { Fragment, useMemo, useState, type FC, type ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

// Cancel/Confirm footer shared by ChangeTripsModal and (in spirit, by hand)
// by ConflictingTripsModal. Kept inside this file because it's used by a
// single component and we don't want to spread the change-trips surface
// across more files than necessary.
interface ChangeTripsFooterProps {
  isLaptopOrBigger: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isConfirmDisabled: boolean;
  isPending: boolean;
  cancelTestId: string;
  confirmTestId: string;
}

const ChangeTripsFooter: FC<ChangeTripsFooterProps> = ({
  isLaptopOrBigger,
  onCancel,
  onConfirm,
  isConfirmDisabled,
  isPending,
  cancelTestId,
  confirmTestId,
}) => {
  const buttonSize = isLaptopOrBigger ? 'regular' : 'large';
  return (
    <div className="flex w-full gap-2 laptop:flex-row laptop:justify-end">
      <Button
        variant="tertiary"
        type="button"
        onClick={onCancel}
        data-testid={cancelTestId}
        size={buttonSize}
        className="rounded-lg laptop:w-auto"
      >
        <TransText i18nKey="cancel" />
      </Button>
      <Button
        type="button"
        onClick={onConfirm}
        isDisabled={isConfirmDisabled}
        isPending={isPending}
        data-testid={confirmTestId}
        size={buttonSize}
        className="rounded-lg laptop:w-auto"
        startAdornment={<Icons.check />}
      >
        <TransText i18nKey="confirm" />
      </Button>
    </div>
  );
};

interface ChangeTripsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TripSection {
  trips: Array<BookedTripDTO>;
  title: ReactNode;
}

// Renders one trip row inside a section. When the section contains more than
// one trip we additionally render the "Trip N" header + passenger popover
// above the card. Dimmed styling kicks in for mixed-offer / non-exchangeable
// trips, where the card itself is also disabled.
const TripRow: FC<{
  trip: BookedTripDTO;
  tripIndex: number;
  changeFlow: ChangeBookingFlow;
  showHeader: boolean;
}> = ({ trip, tripIndex, changeFlow, showHeader }) => {
  const isDimmed = isTripChangeDisabled(trip, changeFlow);
  return (
    <div className="flex flex-col gap-1.5">
      {showHeader && (
        <div className="flex w-full flex-row items-center justify-between">
          <Typography
            variant="body2-bold"
            className={cn(isDimmed && 'text-neutral')}
          >
            <TransText
              i18nKey="tripNumber"
              values={{ number: tripIndex + 1 }}
            />
          </Typography>
          <TripPassengerListPopover trip={trip} isDimmed={isDimmed} />
        </div>
      )}
      <TripOverview trip={trip} changeFlow={changeFlow} />
    </div>
  );
};

const TripSectionBlock: FC<{
  section: TripSection;
  changeFlow: ChangeBookingFlow;
}> = ({ section, changeFlow }) => (
  <div>
    <Typography variant="body1-bold" className="mb-3">
      {section.title}
    </Typography>
    <div className="flex flex-col gap-5">
      {section.trips.map((trip, tripIndex) => (
        <TripRow
          key={trip.id}
          trip={trip}
          tripIndex={tripIndex}
          changeFlow={changeFlow}
          showHeader={section.trips.length > 1}
        />
      ))}
    </div>
  </div>
);

const ChangeTripsModal: FC<ChangeTripsModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isPending, setIsPending] = useState(false);
  const booking = useSelector(manageFlowBookingSelector);
  const { changeFlow } = useChangeBookingSelection();
  const { outboundTrips, inboundTrips } = useBookingTrips(booking);
  const activeOutboundTrips = useMemo(
    () => outboundTrips.filter((trip) => !isTripWhollyInactive(trip)),
    [outboundTrips]
  );
  const activeInboundTrips = useMemo(
    () => inboundTrips.filter((trip) => !isTripWhollyInactive(trip)),
    [inboundTrips]
  );
  const outboundTripId = useSelector(changeTripsOutboundTripIdSelector);
  const returnTripId = useSelector(changeTripsReturnTripIdSelector);
  const isConfirmEnabled = Boolean(outboundTripId || returnTripId);
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });
  const { conflictReason } = useChangeBookingSelection();
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [changeTravelClassAlert, setChangeTravelClassAlert] =
    useState<AlertReason | null>(null);
  const { getChangeTravelClassOffers } = useChangeTravelClass({
    isOutbound: !!outboundTripId,
  });

  const handleConfirm = async () => {
    if (conflictReason !== null) {
      setIsAlertModalOpen(true);
      return;
    }
    dispatch(resetPurchase());
    if (changeFlow === 'changeTrips') {
      navigate('/purchase');
      return;
    }
    if (changeFlow === 'changeTravelClass') {
      setIsPending(true);
      const result = await getChangeTravelClassOffers();
      if (result === true) {
        navigate(
          outboundTripId
            ? '/purchase/outbound/offer'
            : '/purchase/inbound/offer'
        );
      } else {
        setChangeTravelClassAlert(result);
        setIsAlertModalOpen(true);
      }
      setIsPending(false);
    }
  };

  const handleCancel = () => {
    dispatch(resetChangeTripsSelection());
    setIsPending(false);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    dispatch(resetChangeTripsSelection());
    onClose();
  };

  const title: ReactNode = (
    <div className="flex flex-col gap-1 laptop:flex-row laptop:items-baseline laptop:gap-2">
      <Typography variant="subtitle">
        <TransText i18nKey={changeFlow ?? 'changeTrips'} />
      </Typography>
      {booking?.code && (
        <Typography variant="body1" className="text-neutral">
          <TransText i18nKey="booking" /> {booking.code}
        </Typography>
      )}
    </div>
  );

  const sections: Array<TripSection> = [
    {
      trips: activeOutboundTrips,
      title: (
        <TransText
          i18nKey="outboundTrips"
          values={{ count: activeOutboundTrips.length }}
        />
      ),
    },
    {
      trips: activeInboundTrips,
      title: (
        <TransText
          i18nKey="returnTrips"
          values={{ count: activeInboundTrips.length }}
        />
      ),
    },
  ];

  const showDividerBetweenSections =
    !!activeOutboundTrips.length && !!activeInboundTrips.length;

  return (
    <>
      <ChangeTripsModalAlert
        alert={changeTravelClassAlert}
        booking={booking}
        isOpen={isAlertModalOpen}
        onClose={() => {
          setIsAlertModalOpen(false);
          setChangeTravelClassAlert(null);
        }}
      />
      <ResponsiveDialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        title={title}
        footer={
          <ChangeTripsFooter
            isLaptopOrBigger={isLaptopOrBigger}
            isPending={isPending}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            isConfirmDisabled={!isConfirmEnabled}
            cancelTestId="change-trips-cancel"
            confirmTestId="change-trips-confirm"
          />
        }
        content={
          <div
            className="flex flex-col gap-4 p-4"
            data-testid="change-trips-modal-content"
          >
            <Typography variant="body1-bold">
              <TransText i18nKey="selectTrips" />
            </Typography>
            {sections.map((section, index) => {
              if (section.trips.length === 0) return null;
              return (
                <Fragment key={index}>
                  {index > 0 && showDividerBetweenSections && (
                    <Divider variant="medium" />
                  )}
                  <TripSectionBlock
                    section={section}
                    changeFlow={changeFlow ?? 'changeTrips'}
                  />
                </Fragment>
              );
            })}
          </div>
        }
      />
    </>
  );
};

export default ChangeTripsModal;
